<template>
  <li
    class="zm-dropdown-menu__item"
    :class="{
      'is-disabled': disabled,
      'zm-dropdown-menu__item--divided': divided,
      'is-hover':hover,
      [`zm-dropdown-menu__item--${type}`]: type
    }"
    @click="handleClick"
    :aria-disabled="disabled"
    :id="itemId"
    role="menuitem"
    :aria-label="ariaLabel"
    ref="item"
  >
    <span class="zm-dropdown-menu__item-content">
      <i :class="icon" class="mgr-xs" v-if="icon"></i>
      <slot></slot>
    </span>
  </li>
</template>
<script>
  import Emitter from '../../mixins/emitter';

  let _uid = 0;

  export default {
    name: 'ZmDropdownItem',

    mixins: [Emitter],

    props: {
      command: [String, Number, Object, Function],
      disabled: Boolean,
      divided: Boolean,
      icon: String,
      ariaLabel: String,
      type: String
    },

    data() {
      return {
        hover: false
      };
    },

    computed: {
      listId() {
        return this.dropdown.listId;
      },
      itemId() {
        return `dropdown_item_${this.listId}-${_uid++}`;
      }
    },

    inject: ['dropdown'],

    created() {
      this.dropdown.options.push(this);
    },
    watch: {
      disabled() {
        this.$nextTick(() => {
          this.dropdown.initDomOperation();
        });
      }
    },
    beforeDestroy() {
      const index = this.dropdown.options.indexOf(this);
      if (index > -1) {
        this.dropdown.options.splice(index, 1);
      }
    },

    methods: {
      handleClick(e) {
        if (this.disabled) {
          return;
        }
        this.dispatch('ZmDropdown', 'menu-item-click', [this.command, this]);
      }
    }
  };
</script>

<style lang="scss">
  @import "../../scss/mixins/mixins";
  @import "../../scss/common/var";
  @import "../../scss/common/list-var";
  @import "../../scss/token/button-variables";

  // default
  $--dropdown_item-default-color: $--list-text-color;
  $--dropdown_item-default-color-active: $--list-text-primary-color;
  $--dropdown_item-default-background-color-hover: $--list-background--hover;
  $--dropdown_item-default-background-color-active: $--list-background-primary--active;
  // default dark
  $--dropdown_item-default-color-dark: $--list-text-color-dark;
  $--dropdown_item-default-color-active-dark: $--list-text-primary-color-dark;
  $--dropdown_item-default-background-color-hover-dark: $--list-background--hover-dark;
  $--dropdown_item-default-background-color-active-dark: $--list-background-primary--active-dark;
  // primary
  $--dropdown_item-primary-color: map-deep-get($zm-button, 'button.color.label.default.lightMode.quaternary') !default;
  $--dropdown_item-primary-color-active: $--list-text-primary-color-dark;
  $--dropdown_item-primary-background-color-hover: $--list-background--hover;
  $--dropdown_item-primary-background-color-active: $--list-background-primary--active;
  // primary dark
  $--dropdown_item-primary-color-dark: map-deep-get($zm-button, 'button.color.label.default.darkMode.quaternary') !default;
  $--dropdown_item-primary-color-active-dark: $--list-text-primary-color-dark;
  $--dropdown_item-primary-background-color-hover-dark: $--list-background--hover-dark;
  $--dropdown_item-primary-background-color-active-dark: $--list-background-primary--active-dark;
  // danger
  $--dropdown_item-danger-color: map-deep-get($zm-button, 'button.color.label.default.lightMode.negativeQuaternary') !default;
  $--dropdown_item-danger-color-active: $--list-text-primary-color-dark;
  $--dropdown_item-danger-background-color-hover: $--list-background--hover;
  $--dropdown_item-danger-background-color-active: $--list-background-primary--active;
  // danger dark
  $--dropdown_item-danger-color-dark: map-deep-get($zm-button, 'button.color.label.default.darkMode.negativeQuaternary') !default;
  $--dropdown_item-danger-color-active-dark: $--list-text-primary-color-dark;
  $--dropdown_item-danger-background-color-hover-dark: $--list-background--hover-dark;
  $--dropdown_item-danger-background-color-active-dark: $--list-background-primary--active-dark;

  @mixin dropdown-item-variant($color, $color-active, $background-color-hover, $background-color-active) {
    color: $color;
    &:not(.is-disabled):hover,
    &:focus,
    &.is-hover {
      @include e(item-content) {
        background-color: $background-color-hover;
        color: $color;
        a {
          color: $color;
        }
      }
    }
    &:not(.is-disabled):active {
      @include e(item-content) {
        color: $color-active;
        background-color: $background-color-active;
        a {
          color: $color-active;
        }
      }
    }
  }

  @include b(dropdown-menu) {
    @include e(item) {
      list-style: none;
      margin: 0;
      font-size: $--font-size-base;
      color: $--list-text-color;
      cursor: pointer;
      outline: none;

      @include dropdown-item-variant(
        $--dropdown_item-default-color,
        $--dropdown_item-default-color-active,
        $--dropdown_item-default-background-color-hover,
        $--dropdown_item-default-background-color-active
      );

      @include m(primary) {
        @include dropdown-item-variant(
          $--dropdown_item-primary-color,
          $--dropdown_item-primary-color-active,
          $--dropdown_item-primary-background-color-hover,
          $--dropdown_item-primary-background-color-active
        );
      }

      @include m(danger) {
        @include dropdown-item-variant(
          $--dropdown_item-danger-color,
          $--dropdown_item-danger-color-active,
          $--dropdown_item-danger-background-color-hover,
          $--dropdown_item-danger-background-color-active
        );
      }

      @include m(divided) {
        $divided-offset: 4px;
        padding-top: 4px;
        position: relative;
        margin-top: $divided-offset;
        border-top: 1px solid $--border-color-lighter;

        &:before {
          content: '';
          height: $divided-offset;
          display: block;
          position: absolute;
          margin: 0 -$--input-default-padding-horizontal;
          background-color: $--color-white;
        }
      }

      @include when(disabled) {
        cursor: not-allowed;
        color: $--list-text-disable-color;
      }
      a {
        color: $--color-text-primary;
        font-size: 14px;
        display: block;
        &:hover{
          text-decoration: none;
        }
        &:focus{
          outline: none;
        }
      }
    }
    @include e(item-content){
      display: block;
      border-radius: 8px;
      line-height: 24px;
      padding: 4px 8px;
    }
  }

  .zm-theme--dark {
    @include b(dropdown-menu) {
      @include e(item) {
        &.is-disabled {
          color: $--list-text-disable-color-dark;
        }
        @include dropdown-item-variant(
          $--dropdown_item-default-color-dark,
          $--dropdown_item-default-color-active-dark,
          $--dropdown_item-default-background-color-hover-dark,
          $--dropdown_item-default-background-color-active-dark
        );
        a {
          color: $--list-text-primary-color-dark;
        }

        @include m(primary) {
          @include dropdown-item-variant(
            $--dropdown_item-primary-color-dark,
            $--dropdown_item-primary-color-active-dark,
            $--dropdown_item-primary-background-color-hover-dark,
            $--dropdown_item-primary-background-color-active-dark
          );
        }

        @include m(danger) {
          @include dropdown-item-variant(
            $--dropdown_item-danger-color-dark,
            $--dropdown_item-danger-color-active-dark,
            $--dropdown_item-danger-background-color-hover-dark,
            $--dropdown_item-danger-background-color-active-dark
          );
        }

        @include m(divided) {
          border-top-color: #444B53;
        }
      }
    }
  }
  @media (forced-colors: active) {
    .zm-dropdown-menu__item:not(.is-disabled):hover,
    .zm-dropdown-menu__item:focus,
    .zm-dropdown-menu__item.is-hover {
      outline: dotted thin !important;
      outline-offset: -4px;
    }
  }
</style>
